/* eslint-disable no-console */
import React, { useEffect, useRef, useState } from 'react';

import { Button, makeStyles, Typography } from '@material-ui/core';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { enqueueSnackbar } from 'notistack';

import { toolbar } from '../../constants/reportEditor';
import WorkspaceService from '../../services/workspace.service';
import { sanitizeRichText, createSanitizedHtml } from '../../utils/sanitize';
import { createEditorConfig, setupPasteHandler } from '../../utils/editorUtils';

const useStyles = makeStyles(() => ({
  '@global': {
    '.richtexteditor rte-powerby a': {
      cursor: 'default !important',
      opacity: '0 !important',
      pointerEvents: 'none !important',
      width: '0px !important',
    },
  },
  buttonsContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
  },
  editorContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    minHeight: '200px',
  },
  headerContainer: {
    paddingBottom: '24px',
  },
  noReportHeader: {
    color: '#bbb',
  },
}));

const ReportHeader = ({ workspace }) => {
  const [showReportHeaderEditor, setShowReportHeaderEditor] = useState(false);
  const [signatureEditor, setSignatureEditor] = useState(null);
  const [isSavingSignature, setIsSavingSignature] = useState(false);
  const [signatureContent, setSignatureContent] = useState(workspace?.reportHeader || '');

  const signatureEditorRef = useRef(null);
  const classes = useStyles();

  useEffect(() => {
    setSignatureContent(workspace?.reportHeader || '');
  }, [workspace]);

  useEffect(() => {
    return () => {
      if (signatureEditor) {
        const parent = signatureEditorRef.current?.parentElement;
        if (parent) {
          parent.innerHTML = '<div ref="signatureEditorRef"></div>';
        }
        setSignatureEditor(null);
      }
    };
  }, [signatureEditor]);

  const fetchSignatureContent = async () => {
    if (!workspace?.signatureId) return;

    try {
      const workspaceService = new WorkspaceService();
      const signature = await workspaceService.getWorkspaceSignature(workspace.id);

      if (signature?.content) {
        setSignatureContent(signature.content);
      }
    } catch (error) {
      console.error('Error fetching signature:', error);
    }
  };

  useEffect(() => {
    fetchSignatureContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace]);

  const openReportHeaderEditor = () => {
    setShowReportHeaderEditor(true);

    setTimeout(() => {
      const baseConfig = {
        editorResizeMode: 'height',
        enablecontextmenu: false,
        enableobjectresizing: true,
        enterKeyTag: 'div',
        maxImageSize: 2 * 1024 * 1024,
        maxTextLength: 1000,
        showFloatParagraph: false,
        showPlusButton: false,
        showPoweredBy: false,
        showTagList: false,
        toolbar: 'headerToolbar',
        toolbar_headerToolbar: `${toolbar}#{undo,redo}`,
        width: '100%',
      };

      const editorConfig = createEditorConfig(baseConfig);
      const editor = new window.RichTextEditor(signatureEditorRef.current, editorConfig);
      editor.setHTMLCode(signatureContent);
      setupPasteHandler(editor);
      setSignatureEditor(editor);
    }, 100);
  };

  const closeReportHeaderEditor = () => {
    if (signatureEditor) {
      const parent = signatureEditorRef.current?.parentElement;
      if (parent) {
        parent.innerHTML = '<div ref="signatureEditorRef"></div>';
      }
      setSignatureEditor(null);
    }

    setShowReportHeaderEditor(false);
  };

  const saveWorkspaceSignature = async () => {
    if (!workspace?.id) return;

    try {
      setIsSavingSignature(true);
      const rawContent = signatureEditor.getHTMLCode();
      const sanitizedContent = sanitizeRichText(rawContent);
      const workspaceService = new WorkspaceService();

      if (!workspace.signatureId) {
        await workspaceService.createWorkspaceSignature(workspace.id, sanitizedContent);
      } else {
        await workspaceService.updateWorkspaceSignature(workspace.id, sanitizedContent);
      }

      setSignatureContent(sanitizedContent);
      closeReportHeaderEditor();
    } catch (error) {
      console.error('Error saving workspace signature:', error);

      if (error.statusCode === 413 || error.message?.includes('request entity too large') || error.response?.status === 413) {
        enqueueSnackbar('Your header exceeds the maximum allowed size. Please reduce the content or the size or number of images and try again.', {
          autoHideDuration: 8000,
          variant: 'error',
        });
      } else {
        enqueueSnackbar('An error occurred while saving your workspace header. Please try again later.', { autoHideDuration: 8000, variant: 'error' });
      }
    } finally {
      setIsSavingSignature(false);
    }
  };

  return (
    <div className={`${classes.headerContainer} table-container`}>
      <Typography className="secondary-header mb-1">Reports&apos; Default Header</Typography>

      {showReportHeaderEditor && (
        <div className={classes.editorContainer}>
          <div className="editor-container">
            <div ref={signatureEditorRef} style={{ minHeight: '200px' }} />
          </div>

          <div className={classes.buttonsContainer}>
            <Button variant="outlined" color="secondary" onClick={closeReportHeaderEditor}>
              Cancel
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={saveWorkspaceSignature}
              disabled={isSavingSignature}
              startIcon={<SaveOutlinedIcon />}
              style={{ marginLeft: '8px' }}>
              {isSavingSignature ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}
      {!showReportHeaderEditor && (
        <>
          {signatureContent ? (
            // eslint-disable-next-line react/no-danger
            <div dangerouslySetInnerHTML={createSanitizedHtml(signatureContent)} />
          ) : (
            <Typography className={classes.noReportHeader}>No header is set for this workspace</Typography>
          )}
          <Button variant="contained" color="primary" onClick={openReportHeaderEditor} startIcon={<EditNoteIcon />} className="mt-1">
            Edit Header
          </Button>
        </>
      )}
    </div>
  );
};

export default ReportHeader;
